import { useState, useEffect } from 'preact/hooks';
import { api } from '../../api/client';
import type { SkillMeta, Machine } from './types';
import { SkeletonBar } from '../ui';
import { T } from '../../styles/tokens';
import { Button } from '../ui';

interface Props {
  skill: SkillMeta | null;
  onClose: () => void;
}

export function InstallModal({ skill, onClose }: Props) {
  const [machines, setMachines] = useState<Machine[]>([]);
  const [selectedMachine, setSelectedMachine] = useState('');
  const [installing, setInstalling] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get<any>('/runner-v3/list')
      .then((r: any) => {
        const list = r?.data?.runners || r?.runners || [];
        setMachines(list);
        if (list.length > 0) setSelectedMachine(list[0].id);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (!skill) return null;

  const handleInstall = async () => {
    if (!selectedMachine) return;
    setInstalling(true);
    setResult(null);
    try {
      await api.post(`/runner-v3/${selectedMachine}/skills/install`, { skillName: skill.name });
      setResult({ ok: true, msg: `${skill.name} instalada com sucesso!` });
    } catch (e: any) {
      setResult({ ok: false, msg: e?.message || 'Erro ao instalar' });
    } finally {
      setInstalling(false);
    }
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: T.bgOverlay, zIndex: 2000 }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: '420px', maxWidth: '90vw', background: T.bg,
        border: `1px solid ${T.purpleBgStrong}`, borderRadius: '16px',
        padding: '24px', zIndex: 2001, animation: 'fadeInUp 0.3s ease',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
 <span style={{ fontSize: '28px' }}>{skill.emoji || ''}</span>
          <div>
            <div style={{ fontSize: '16px', fontWeight: 700, color: T.text }}>Instalar {skill.name}</div>
            <div style={{ fontSize: '12px', color: T.textSecondary }}>Selecione a máquina</div>
          </div>
          <div style={{ flex: 1 }} />
          <Button variant="ghost" onClick={onClose} style={{ background: 'none', border: 'none', color: T.textSecondary, fontSize: '18px', cursor: 'pointer' }}></Button>
        </div>

        {loading ? (
          <div style={{ padding: 16 }}><SkeletonBar width="60%" height={13} style={{ marginBottom: 8 }} /><SkeletonBar width="40%" height={13} /></div>
        ) : machines.length === 0 ? (
          <div style={{ color: T.warning, fontSize: '13px', textAlign: 'center', padding: '20px' }}>Nenhuma máquina encontrada</div>
        ) : (
          <select
            value={selectedMachine}
            onChange={(e: any) => setSelectedMachine(e.target.value)}
            style={{
              width: '100%', padding: '10px 14px', borderRadius: '10px',
              background: T.purpleBg, border: `1px solid ${T.purpleBgStrong}`,
              color: T.text, fontSize: '14px', marginBottom: '16px', outline: 'none',
            }}
          >
            {machines.map(m => (
              <option key={m.id} value={m.id} style={{ background: T.bg }}>{m.name || m.id}</option>
            ))}
          </select>
        )}

        {result && (
          <div style={{
            padding: '10px 14px', borderRadius: '8px', marginBottom: '12px', fontSize: '13px',
            background: result.ok ? T.successBg : T.dangerBg,
            color: result.ok ? T.success : T.danger,
            border: `1px solid ${result.ok ? T.successBg : T.dangerBg}`,
          }}>
            {result.msg}
          </div>
        )}

        <Button variant="ghost"
          onClick={handleInstall}
          disabled={installing || !selectedMachine || machines.length === 0}
          style={{
            width: '100%', padding: '12px', borderRadius: '10px', border: 'none',
            background: installing ? T.purpleBg : 'linear-gradient(135deg, #7c3aed, #a855f7)',
            color: T.text, fontSize: '14px', fontWeight: 600, cursor: installing ? 'not-allowed' : 'pointer',
          }}
        >
          {installing ? 'Instalando...' : 'Instalar'}
        </Button>
      </div>
    </>
  );
}
